import { Component } from '@angular/core';
import { Observable, of, switchMap, catchError } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { LocationService } from '@app/core/location';
import { MsgboxService } from '@app/shared/msgbox';

import {
  IPatientHistory,
  PatientRequestsService,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users/users.service';
import { SortEvent } from 'primeng/api';
import * as moment from 'moment';

@Component({
  selector: 'app-patient-history-list',
  templateUrl: './patient-history-list.component.html',
})
export class PatientHistoryListComponent {
  public records$: Observable<IPatientHistory[]>;
  public records: IPatientHistory[] = [];
  public columns: string[] = [];
  private state: IPatientHistory[] | null = null;

  public sortField: string = 'nhc';
  public sortOrder: number = 1;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private locationService: LocationService,
    private service: PatientRequestsService,
    private userService: UsersService,
    private msgbox: MsgboxService
  ) {
    this.state = this.router.getCurrentNavigation()?.extras.state?.['data'];
  }

  public ngOnInit(): void {
    if (!this.state) {
      this.records$ = this.route.queryParams.pipe(
        switchMap((prms) =>
          this.service.getPatientHistories(
            this.locationService.decodeParams(prms)
          ).pipe(
            catchError((err) => {
              const key = err?.error?.message || err?.message;
              if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
                this.msgbox.showError(key);
              } else {
                this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
              }
              return of([]);
            })
          )
        )
      );
    } else {
      this.records$ = new Observable<IPatientHistory[]>((observer) => {
        observer.next(this.state);
        observer.complete();
      });
    }

    this.records$.subscribe((data) => {
      this.records = (data || []).map((record) => {
        return {
          ...record,
          fechaNac: record.fechaNac
            ? moment(record.fechaNac, 'DD/MM/YYYY').toDate()
            : null,
        };
      });
      if (this.records.length > 0 && this.records[0].columnas) {
        this.columns = this.buildColumns(this.records[0].columnas);
        const [_sortField, _sortOrder] = this.records[0].orden?.split('+') ?? ['nhc', 'ASC'];
        this.sortField = this.convertTitleCaseToCamelCase(_sortField);
        this.sortOrder = _sortOrder === 'ASC' ? 1 : -1;
      } else {
        this.columns = [];
        this.sortField = 'nhc';
        this.sortOrder = 1;
      }
    });
  }

  private convertTitleCaseToCamelCase(title: string): string {
    // convert first char to lowercase
    return title.charAt(0).toLowerCase() + title.slice(1);
  }

  private buildColumns(columns: string): string[] {
    return columns.split(';').map((col) => col?.trim().toLowerCase() || '');
  }

  public onSort(event: SortEvent): void {
    this.records$ = this.records$.pipe(
      switchMap((data) => {
        return new Observable<IPatientHistory[]>((observer) => {
          observer.next(
            [...data].sort((a, b) => {
              let value1 = a[event.field];
              let value2 = b[event.field];

              if (event.field === 'fechaNac') {
                value1 = this.parseDate(value1);
                value2 = this.parseDate(value2);
              }

              let result = null;
              if (value1 < value2) {
                result = -1;
              } else if (value1 > value2) {
                result = 1;
              }
              return event.order * result;
            })
          );
          observer.complete();
        });
      })
    );
  }

  private parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split('/');
    return new Date(Number(year), Number(month) - 1, Number(day)); // Month is 0-indexed
  }

  public onRowSelect($event): void {
    const record = $event.data as IPatientHistory;
    const centroId = this.userService.getSisifoDefaultCenter();
    let searchType: string | undefined;
    try {
      const decoded = this.locationService.decodeParams<any>(this.route.snapshot.queryParams);
      searchType = decoded?.searchType;
    } catch {
      // ignore if params cannot be decoded
    }
    const queryParams: any = centroId != null ? { centroId } : {};
    if (searchType) {
      queryParams.searchType = searchType;
    }
    this.router.navigate(['../patient', record.historia], {
      relativeTo: this.route,
      queryParams,
      skipLocationChange: false,
    });
  }
}
